import type { PaletteMode, PaletteOptions } from "@mui/material/styles";
import { createTheme } from "@mui/material/styles";
import lightTheme from "./light.json";
import darkTheme from "./dark.json";
import OpenSansRegularUrl from "@/assets/fonts/OpenSans/OpenSans-Regular.ttf?url";
import OpenSansMediumUrl from "@/assets/fonts/OpenSans/OpenSans-Medium.ttf?url";
import OpenSansSemiBoldUrl from "@/assets/fonts/OpenSans/OpenSans-SemiBold.ttf?url";
import OpenSansBoldUrl from "@/assets/fonts/OpenSans/OpenSans-Bold.ttf?url";
// Noto Sans для валютных символов (unicode-range fallback)
import NotoSansRegularUrl from "@/assets/fonts/NotoSans/NotoSans-Regular.ttf?url";
import NotoSansMediumUrl from "@/assets/fonts/NotoSans/NotoSans-Medium.ttf?url";
import NotoSansSemiBoldUrl from "@/assets/fonts/NotoSans/NotoSans-SemiBold.ttf?url";
import NotoSansBoldUrl from "@/assets/fonts/NotoSans/NotoSans-Bold.ttf?url";

/**
 * Unicode ranges для валютных символов:
 * - U+0024: $ (Dollar Sign)
 * - U+00A2-00A5: ¢, £, ¤, ¥
 * - U+20A0-20CF: Currency Symbols block (₽, €, ₴, ₸, ₿ и др.)
 */
const CURRENCY_UNICODE_RANGE = "U+0024, U+00A2-00A5, U+20A0-20CF";

// Declare module to extend MUI theme with custom breakpoints
declare module "@mui/material/styles" {
  interface BreakpointOverrides {
    xs: true;
    sm: true;
    md: true;
    lg: true;
    l: true;
    xl: true;
    x2l: true;
    x3l: true;
    x4l: true;
    x5l: true;
    x6l: true;
    x7l: true;
  }
}

export const breakpoints = {
  values: {
    xs: 0,
    sm: 370,
    md: 400,
    lg: 500,
    l: 600,
    xl: 700,
    x2l: 900,
    x3l: 1200,
    x4l: 1500,
    x5l: 1900,
    x6l: 2300,
    x7l: 2600,
  },
};

export const createAppTheme = (
  mode: PaletteMode,
  palette: PaletteOptions = { mode } as PaletteOptions,
) => {
  const defaultPalette = palette.mode === "light" ? lightTheme : darkTheme;

  return createTheme({
    breakpoints,
    palette: {
      ...defaultPalette,
      ...palette,
      mode,
    },
    typography: {
      fontFamily: "Open Sans, sans-serif",
      fontWeightLight: 300,
      fontWeightRegular: 400,
      fontWeightMedium: 500,
      fontWeightBold: 600,
      h1: {
        fontWeight: 700,
      },
      h2: {
        fontWeight: 700,
      },
      h3: {
        fontWeight: 700,
      },
      h4: {
        fontWeight: 700,
      },
      h5: {
        fontWeight: 700,
      },
      h6: {
        fontWeight: 700,
      },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: (theme) => `
          /* Noto Sans для валютных символов (unicode-range fallback) */
          @font-face {
            font-family: 'Open Sans';
            font-style: normal;
            font-display: swap;
            font-weight: 400;
            src: url(${NotoSansRegularUrl}) format('truetype');
            unicode-range: ${CURRENCY_UNICODE_RANGE};
          }
          @font-face {
            font-family: 'Open Sans';
            font-style: normal;
            font-display: swap;
            font-weight: 500;
            src: url(${NotoSansMediumUrl}) format('truetype');
            unicode-range: ${CURRENCY_UNICODE_RANGE};
          }
          @font-face {
            font-family: 'Open Sans';
            font-style: normal;
            font-display: swap;
            font-weight: 600;
            src: url(${NotoSansSemiBoldUrl}) format('truetype');
            unicode-range: ${CURRENCY_UNICODE_RANGE};
          }
          @font-face {
            font-family: 'Open Sans';
            font-style: normal;
            font-display: swap;
            font-weight: 700;
            src: url(${NotoSansBoldUrl}) format('truetype');
            unicode-range: ${CURRENCY_UNICODE_RANGE};
          }
          /* Open Sans - основной шрифт */
          @font-face {
            font-family: 'Open Sans';
            font-style: normal;
            font-display: swap;
            font-weight: 400;
            src: local('Open Sans'), local('Open Sans Regular'), url(${OpenSansRegularUrl}) format('truetype');
          }
          @font-face {
            font-family: 'Open Sans';
            font-style: normal;
            font-display: swap;
            font-weight: 500;
            src: local('Open Sans'), local('Open Sans Medium'), url(${OpenSansMediumUrl}) format('truetype');
          }
          @font-face {
            font-family: 'Open Sans';
            font-style: normal;
            font-display: swap;
            font-weight: 600;
            src: local('Open Sans'), local('Open Sans SemiBold'), url(${OpenSansSemiBoldUrl}) format('truetype');
          }
          @font-face {
            font-family: 'Open Sans';
            font-style: normal;
            font-display: swap;
            font-weight: 700;
            src: local('Open Sans'), local('Open Sans Bold'), url(${OpenSansBoldUrl}) format('truetype');
          }
          #root {
            background-color: ${theme.palette.background.default};
          }
          /* Глобальные стили скроллбара */
          * {
            scrollbar-width: thin;
            scrollbar-color: ${theme.palette.action.selected} transparent;
          }
          *::-webkit-scrollbar {
            width: 6px;
            height: 6px;
          }
          *::-webkit-scrollbar-track {
            background: transparent;
            border-radius: 3px;
          }
          *::-webkit-scrollbar-thumb {
            background-color: ${theme.palette.action.selected};
            border-radius: 3px;
          }
          *::-webkit-scrollbar-thumb:hover {
            background-color: ${theme.palette.action.active};
          }
        `,
      },
      MuiButton: {
        styleOverrides: {
          root: {
            height: 52,
            borderRadius: 12,
            textTransform: "none",
            fontWeight: 600,
            fontSize: 16,
          },
          sizeSmall: {
            height: 36,
          },
        },
      },
    },
  });
};
